import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { COMPANY } from "@/content";
import CallLink from "@/components/CallLink";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Reviews", to: "/#reviews" },
  { label: "Contact", to: "/#contact" },
];

export const scrollToTop = () => {
  if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1 });
  else window.scrollTo({ top: 0, behavior: "smooth" });
};

export const Logo = () => {
  const location = useLocation();
  return (
    <Link to="/" data-testid="logo-link" onClick={(e) => { if (location.pathname === "/") { e.preventDefault(); scrollToTop(); } }} className="group inline-flex items-center">
      <img src="/logo.png" alt="Juli Klein's A/C Services, LLC" className="h-16 md:h-[4.75rem] w-auto transition-transform duration-300 group-hover:-translate-y-0.5 [filter:drop-shadow(0_1px_5px_rgba(255,255,255,0.55))]" />
    </Link>
  );
};

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const lightHero = location.pathname === "/services";
  const dark = scrolled || lightHero;

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72, duration: 1.1 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  const goTo = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") { navigate("/"); setTimeout(() => scrollToId(id), 700); }
      else scrollToId(id);
    } else if (to === location.pathname) {
      scrollToTop();
    } else navigate(to);
  };

  return (
    <header data-testid="site-header" className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? "bg-bone/80 backdrop-blur-xl border-b border-sand" : "bg-transparent"}`}>
      <div className="mx-auto max-w-7xl px-5 md:px-10 h-24 flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <button key={n.label} data-testid={`nav-${n.label.toLowerCase()}`} onClick={() => goTo(n.to)}
              className={`text-sm font-medium transition-colors duration-200 hover:text-terracotta ${dark ? "text-charcoal/80" : "text-bone/90"}`}>{n.label}</button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <CallLink data-testid="header-call-btn"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-sm font-semibold text-bone hover:bg-terracotta-dark hover:-translate-y-0.5 transition-all duration-200 shadow-sm">
            <Phone size={15} /> {COMPANY.phone}
          </CallLink>
          <CallLink data-testid="header-call-icon" aria-label={`Call ${COMPANY.phone}`}
            className="sm:hidden grid place-items-center h-10 w-10 rounded-full bg-terracotta text-bone shadow-sm active:scale-95 transition-transform">
            <Phone size={18} />
          </CallLink>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="lg:hidden grid place-items-center h-10 w-10 rounded-full bg-sand text-charcoal">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-bone border-b border-sand">
            <div className="px-5 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <button key={n.label} data-testid={`mobile-nav-${n.label.toLowerCase()}`} onClick={() => goTo(n.to)}
                  className="text-left py-3 text-base font-medium text-charcoal border-b border-sand/60 last:border-0">{n.label}</button>
              ))}
              <CallLink className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-5 py-3 text-sm font-semibold text-bone">
                <Phone size={15} /> {COMPANY.phone}
              </CallLink>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
